import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useSortableData, SortableHead } from "@/hooks/useSortableData";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, ShieldCheck, Lock, Loader2, ChevronDown } from "lucide-react";
import { toast } from "sonner";

export default function Roles() {
  const [roles, setRoles] = useState([]);
  const [perms, setPerms] = useState([]);
  const [editing, setEditing] = useState(null); // null | {} new | role edit
  const [name, setName] = useState("");
  const [selected, setSelected] = useState([]);
  const [openGroups, setOpenGroups] = useState([]);
  const [saving, setSaving] = useState(false);
  const [deleteRole, setDeleteRole] = useState(null);

  const load = () => api.get("/roles").then(({ data }) => setRoles(data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/permissions").then(({ data }) => setPerms(data)).catch(() => {});
  }, []);

  const groups = useMemo(() => {
    const g = {};
    perms.forEach((p) => { (g[p.group] = g[p.group] || []).push(p); });
    return g;
  }, [perms]);

  const openNew = () => { setEditing({}); setName(""); setSelected([]); setOpenGroups([]); };
  const openEdit = (r) => {
    setEditing(r); setName(r.name);
    const sel = r.permissions || [];
    setSelected(sel);
    setOpenGroups([...new Set(perms.filter((p) => sel.includes(p.key)).map((p) => p.group))]);
  };

  const toggleGroup = (g) => setOpenGroups((s) => (s.includes(g) ? s.filter((x) => x !== g) : [...s, g]));

  const toggle = (key) => setSelected((s) => (s.includes(key) ? s.filter((k) => k !== key) : [...s, key]));

  const save = async () => {
    if (!editing.id && !name.trim()) { toast.error("Role name is required"); return; }
    setSaving(true);
    try {
      if (editing.id) {
        await api.put(`/roles/${editing.id}`, { permissions: selected });
        toast.success("Role updated");
      } else {
        await api.post("/roles", { name: name.trim(), permissions: selected });
        toast.success("Role created");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const doDelete = async () => {
    try { await api.delete(`/roles/${deleteRole.id}`); toast.success("Role deleted"); setDeleteRole(null); await load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const sort = useSortableData(roles);
  const sorted = sort.sorted;

  return (
    <div data-testid="roles-page">
      <div className="flex justify-between items-start mb-6 gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-emerald-600 font-medium">Administration</p>
          <h1 className="font-heading text-2xl font-bold text-slate-900 tracking-tight">Roles &amp; Permissions</h1>
          <p className="text-sm text-slate-500 mt-1">Create roles and assign privileges. Assign roles to users in User Management.</p>
        </div>
        <Button onClick={openNew} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="role-add-btn">
          <Plus className="h-4 w-4 mr-2" /> Add Role
        </Button>
      </div>

      <Card className="border-slate-200 shadow-sm">
        <Table>
          <TableHeader>
            <TableRow>
              <SortableHead column="name" sort={sort}>Role</SortableHead>
              <TableHead>Privileges</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.map((r) => (
              <TableRow key={r.id} className="hover:bg-slate-50" data-testid={`role-row-${r.name}`}>
                <TableCell className="font-medium">
                  <span className="inline-flex items-center gap-2">
                    <ShieldCheck className="h-4 w-4 text-emerald-600" />
                    <span className="capitalize">{r.name.replace(/_/g, " ")}</span>
                    {r.is_system && <Badge variant="secondary" className="text-[10px]"><Lock className="h-3 w-3 mr-1" />System</Badge>}
                  </span>
                </TableCell>
                <TableCell className="text-slate-600 text-sm">
                  {r.is_system ? "All privileges" : (r.permissions?.length ? `${r.permissions.length} privilege(s)` : "No privileges")}
                </TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  {r.is_system ? (
                    <span className="text-xs text-slate-400">Protected</span>
                  ) : (
                    <>
                      <Button variant="ghost" size="sm" onClick={() => openEdit(r)} data-testid={`role-edit-${r.name}`}><Pencil className="h-4 w-4 text-slate-600" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => setDeleteRole(r)} data-testid={`role-delete-${r.name}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] p-0 gap-0 flex flex-col overflow-hidden" data-testid="role-form-dialog">
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-200 shrink-0">
            <DialogTitle className="font-heading">{editing?.id ? "Edit Role" : "Add Role"}</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">Select the privileges this role grants.</DialogDescription>
          </DialogHeader>
          <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 space-y-5 form-scroll">
            <div className="space-y-2">
              <Label>Role Name</Label>
              <Input value={name} disabled={!!editing?.id} onChange={(e) => setName(e.target.value)} placeholder="e.g. Field Assessor" data-testid="role-name-input" />
            </div>
            {Object.entries(groups).map(([group, list]) => {
              const sel = list.filter((p) => selected.includes(p.key)).length;
              const isOpen = openGroups.includes(group);
              return (
                <div key={group} className="border border-slate-200 rounded-md overflow-hidden">
                  <button
                    type="button"
                    onClick={() => toggleGroup(group)}
                    className="w-full flex items-center justify-between px-3 py-2.5 text-left hover:bg-slate-50 transition-colors"
                    data-testid={`perm-group-${group}`}
                  >
                    <span className="text-xs font-semibold uppercase tracking-wide text-emerald-700">
                      {group}
                      {sel > 0 && <span className="ml-2 normal-case font-normal text-slate-500">({sel} selected)</span>}
                    </span>
                    <ChevronDown className={`h-4 w-4 text-slate-500 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                  </button>
                  {isOpen && (
                    <div className="px-3 pb-3 pt-1 space-y-2 border-t border-slate-100">
                      {list.map((p) => (
                        <label key={p.key} className="flex items-start gap-3 cursor-pointer rounded-md p-2 hover:bg-slate-50">
                          <Checkbox checked={selected.includes(p.key)} onCheckedChange={() => toggle(p.key)} data-testid={`perm-${p.key}`} />
                          <span className="text-sm text-slate-800">{p.label}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-white shrink-0">
            <Button variant="outline" onClick={() => setEditing(null)} data-testid="role-cancel-btn">Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="role-save-btn">
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteRole} onOpenChange={(o) => !o && setDeleteRole(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete role "{deleteRole?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>Users assigned this role must be reassigned first. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="role-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-500 hover:bg-red-600" data-testid="role-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
